import { useCallback, useEffect, useRef, useState } from 'react';

/* Contador de camadas que travam a rolagem, no escopo do módulo. Salvar e
   restaurar o `overflow` anterior falha quando uma camada abre a partir de outra:
   a segunda captura "hidden" como estado original e nunca libera a rolagem.
   Contando, o desbloqueio acontece quando a última fecha. */
let travadas = 0;

/* Pilha das camadas abertas. Só a do topo responde ao Esc: com um diálogo aberto
   por cima do painel do Brain, Esc fecha o diálogo e deixa o painel. */
const pilha: symbol[] = [];

const SELETOR_FOCAVEL =
    'a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), ' +
    'textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const SELETOR_PRIMEIRO =
    'input:not([type=hidden]):not([disabled]), textarea:not([disabled]), ' +
    'select:not([disabled]), button:not([disabled])';

/* Descarta o que está de fato oculto, não o que tem tamanho zero. Medir por
   `offsetWidth` parece equivalente e não é: um elemento pode ser focável e ter
   caixa nula, e ambientes sem layout — jsdom, prerender — devolvem 0 para tudo,
   o que transformava o trap num laço vazio e desligava a circulação de Tab. */
function estaVisivel(el: HTMLElement): boolean {
    if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') return false;
    const estilo = getComputedStyle(el);
    return estilo.display !== 'none' && estilo.visibility !== 'hidden';
}

/**
 * Põe a camada na pilha do Esc: enquanto aberta e no topo, Esc chama `aoEsc` e
 * não chega às camadas de baixo. Menus e seletores não modais usam só isto; o
 * `useCamada` usa isto e o resto.
 */
export function useEscNaPilha(aberta: boolean, aoEsc?: () => void): void {
    const esc = useRef(aoEsc);
    esc.current = aoEsc;
    /* Ouvinte na janela, em captura, para vir antes dos ouvintes das telas. Só a
       camada do topo age, e só ela para a propagação. */
    useEffect(() => {
        if (!aberta) return undefined;
        const id = Symbol('camada');
        pilha.push(id);
        const aoTeclar = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || pilha[pilha.length - 1] !== id || !esc.current) return;
            e.stopPropagation();
            esc.current();
        };
        window.addEventListener('keydown', aoTeclar, true);
        return () => {
            window.removeEventListener('keydown', aoTeclar, true);
            const pos = pilha.indexOf(id);
            if (pos >= 0) pilha.splice(pos, 1);
        };
    }, [aberta]);
}

export interface OpcoesDaCamada {
    aberta: boolean;
    /** O que o Esc faz: fechar, ou pedir confirmação. Sem ele, Esc não faz nada. */
    aoEsc?: () => void;
    /** Trava a rolagem do `body` enquanto aberta. Padrão: sim. */
    travarRolagem?: boolean;
    /** Onde o foco entra ao abrir. Padrão: o primeiro campo ou botão da camada. */
    seletorDoPrimeiroFoco?: string;
}

/**
 * O comportamento de toda camada modal, num lugar só: Esc (só a do topo), trava
 * de rolagem, foco que entra ao abrir e VOLTA a quem abriu, e Tab circulando por
 * dentro. O `Modal` usa; as camadas do Brain, que têm o visual do app original,
 * usam o mesmo — e não uma segunda implementação que divergiria na primeira
 * correção de foco.
 *
 * Devolve o `ref` do contêiner da camada (o elemento com `role="dialog"`).
 */
export function useCamada<T extends HTMLElement = HTMLDivElement>({
    aberta, aoEsc, travarRolagem = true, seletorDoPrimeiroFoco = SELETOR_PRIMEIRO,
}: OpcoesDaCamada): (no: T | null) => void {
    const [no, setNo] = useState<T | null>(null);
    const ultimoNo = useRef<T | null>(null);
    const foiFocado = useRef<Element | null>(null);

    const ref = useCallback((el: T | null) => {
        if (el) ultimoNo.current = el;
        setNo(el);
    }, []);

    /* Sem `aoEsc`, a camada ainda ocupa o topo da pilha: Esc não fecha o que está embaixo. */
    useEscNaPilha(aberta, aoEsc ?? (() => undefined));

    /* Bloqueia a rolagem de fundo. Sem isso, rolar dentro da camada continua na
       página atrás quando o conteúdo chega ao fim — e a camada parece deslizar. */
    useEffect(() => {
        if (!aberta || !travarRolagem) return undefined;
        travadas += 1;
        document.body.style.overflow = 'hidden';
        return () => {
            travadas = Math.max(0, travadas - 1);
            if (travadas === 0) document.body.style.overflow = '';
        };
    }, [aberta, travarRolagem]);

    /* Foco: guarda quem abriu e, ao fechar, devolve a ele. Sem o retorno, quem
       navega por teclado é jogado para o começo da página e perde o lugar. */
    useEffect(() => {
        if (!aberta) return undefined;
        foiFocado.current = document.activeElement;
        return () => {
            const caixa = ultimoNo.current;
            /* Só devolve o foco se ele ainda estiver aqui dentro (ou solto no body).
               Sem esta guarda, fechar A devolvia o foco ao gatilho de A mesmo depois
               de B ter assumido — e a segunda camada perdia o foco para a primeira. */
            const ativo = document.activeElement;
            const focoAindaNosso = !ativo || ativo === document.body || Boolean(caixa?.contains(ativo));
            if (!focoAindaNosso) return;

            const volta = foiFocado.current;
            /* Gatilho ainda no DOM: devolve o foco a ele. */
            if (volta instanceof HTMLElement && document.contains(volta) && volta !== document.body) {
                volta.focus();
                return;
            }
            /* Gatilho removido (a linha que abriu a camada foi excluída nela mesma):
               cair no `body` deixa o foco em lugar nenhum e o próximo Tab recomeça a
               página. O conteúdo principal é o ponto de retorno seguro. */
            const principal = document.querySelector('main') ?? document.body;
            if (principal instanceof HTMLElement) {
                if (!principal.hasAttribute('tabindex')) principal.setAttribute('tabindex', '-1');
                principal.focus();
            }
        };
    }, [aberta]);

    /* Foco entra na camada assim que o contêiner existe. */
    useEffect(() => {
        if (!aberta || !no) return undefined;
        const t = setTimeout(() => {
            const alvo = no.querySelector<HTMLElement>(seletorDoPrimeiroFoco);
            (alvo ?? no).focus();
        }, 30);
        return () => clearTimeout(t);
    }, [aberta, no, seletorDoPrimeiroFoco]);

    /* Focus trap: Tab circula dentro da camada. Tabular para fora de um
       `aria-modal` alcança conteúdo que o leitor de tela considera inerte. O
       ouvinte vive no nó, não numa prop JSX: assim o `role="dialog"` não carrega
       um manipulador de teclado que as regras de acessibilidade leem como
       elemento interativo improvisado. */
    useEffect(() => {
        if (!aberta || !no) return undefined;
        const aoTabular = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;
            const focaveis = [...no.querySelectorAll<HTMLElement>(SELETOR_FOCAVEL)].filter(estaVisivel);
            if (focaveis.length === 0) return;
            const primeiro = focaveis[0];
            const ultimo = focaveis[focaveis.length - 1];
            if (!primeiro || !ultimo) return;
            if (e.shiftKey && document.activeElement === primeiro) {
                e.preventDefault();
                ultimo.focus();
            } else if (!e.shiftKey && document.activeElement === ultimo) {
                e.preventDefault();
                primeiro.focus();
            }
        };
        no.addEventListener('keydown', aoTabular);
        return () => no.removeEventListener('keydown', aoTabular);
    }, [aberta, no]);

    return ref;
}
